import { ArrowBack, Settings } from '@mui/icons-material';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { alpha, styled } from '@mui/material/styles';
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { configItems } from '../configurations/tableData';
import { tokens } from '../../mui-theme';
import { useProject } from '../../context/ProjectContext';
import { useAccess } from '../../hooks/useAccess';

const drawerWidth = 232;

const Drawer = styled(MuiDrawer)(() => ({
  '& .MuiDrawer-paper': {
    position: 'relative',
    width: drawerWidth,
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    borderRight: `1px solid ${tokens.colors.neutral[200]}`,
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
}));

const ADMIN_ONLY_TABS = new Set(['ai-config']);

const ConfigSidebar = ({ selectedTab, setSelectedTab, user }) => {
  const { currentProject } = useProject();
  const { projectId } = useParams();
  const { canAccess } = useAccess();


  return (
    <Drawer variant="permanent" open={true}>
      <Toolbar />

      {/* ── Header ─────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1.5,
          borderBottom: `1px solid ${tokens.colors.neutral[200]}`,
        }}
      >
        {/* Back link */}
        <Box
          component={Link}
          to={`/project/${projectId}/dashboard`}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            color: tokens.colors.neutral[500],
            textDecoration: 'none',
            fontSize: '0.78rem',
            fontWeight: 500,
            transition: 'color 0.15s',
            '&:hover': { color: tokens.colors.primary[500] },
          }}
        >
          <ArrowBack sx={{ fontSize: 14 }} />
          Back to Project
        </Box>

        {/* Project name card */}
        {currentProject && (
          <Box
            sx={{
              mt: 1.25,
              p: 1.25,
              borderRadius: '8px',
              backgroundColor: alpha(tokens.colors.primary[500], 0.06),
              border: `1px solid ${alpha(tokens.colors.primary[500], 0.12)}`,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '6px',
                backgroundColor: tokens.colors.primary[500],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Settings sx={{ fontSize: 14, color: '#fff' }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: tokens.colors.neutral[400],
                  mb: 0.25,
                }}
              >
                Configuration
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  color: tokens.colors.neutral[800],
                  lineHeight: 1.3,
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                }}
              >
                {currentProject.name}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Nav ────────────────────────────────────────────────── */}
      <List component="nav" disablePadding sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {Object.keys(configItems).map((category) => {
          const visibleItems = configItems[category].filter(
            (item) => !ADMIN_ONLY_TABS.has(item.id) || canAccess('ManageAiConfigProject')
          );
          if (!visibleItems.length) return null;

          return (
            <React.Fragment key={category}>
              {/* Category pill — matches original blue header style but refined */}
              <Box
                sx={{
                  mx: 1.5,
                  mt: 1,
                  mb: 0.5,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: '6px',
                  backgroundColor: tokens.colors.primary[500],
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    color: '#fff',
                    textTransform: 'capitalize',
                  }}
                >
                  {category}
                </Typography>
              </Box>

              {visibleItems.map((item) => {
                const isActive = selectedTab === item.id;
                return (
                  <Box key={item.id} sx={{ px: 1, mb: 0.25 }}>
                    <ListItemButton
                      component={Link}
                      to={`/project/${projectId}/configurations?tab=${item.id}`}
                      selected={isActive}
                      onClick={() => setSelectedTab(item.id)}
                      sx={{
                        borderRadius: '6px',
                        py: 0.75,
                        px: 1.5,
                        minHeight: 36,
                        position: 'relative',
                        backgroundColor: isActive
                          ? alpha(tokens.colors.primary[500], 0.08)
                          : 'transparent',
                        transition: 'background-color 0.15s',
                        '&:hover': {
                          backgroundColor: isActive
                            ? alpha(tokens.colors.primary[500], 0.11)
                            : tokens.colors.neutral[100],
                        },
                        '&.Mui-selected': {
                          backgroundColor: alpha(tokens.colors.primary[500], 0.08),
                          '&:hover': {
                            backgroundColor: alpha(tokens.colors.primary[500], 0.11),
                          },
                        },
                        ...(isActive && {
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: '18%',
                            height: '64%',
                            width: 3,
                            borderRadius: '0 3px 3px 0',
                            backgroundColor: tokens.colors.primary[500],
                          },
                        }),
                      }}
                    >
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.8125rem',
                          fontWeight: isActive ? 600 : 400,
                          color: isActive
                            ? tokens.colors.primary[500]
                            : tokens.colors.neutral[700],
                          lineHeight: 1.4,
                          noWrap: true,
                        }}
                        sx={{ m: 0 }}
                      />
                    </ListItemButton>
                  </Box>
                );
              })}
            </React.Fragment>
          );
        })}
      </List>
    </Drawer>
  );
};

export default ConfigSidebar;